// chat.schema.ts — Zod contract for the Rheora AI chatbot host.
//
// ─── CONTRACT PROVENANCE ───────────────────────────────────────────────────
//   POST {VITE_AI_CHAT_API_URL}/api/v1/chat
//
// A FastAPI service (`rheora-crm-chatbot`, version 0.1.0) doing text-to-SQL over
// the `rheora-usa` Postgres with Azure OpenAI. Shapes below were taken from its
// live `/openapi.json` on 2026-09-22, not from prose docs, and verified against
// real responses.
//
// ─── WIRE-SHAPE RULES ──────────────────────────────────────────────────────
//
// 1. **Only FIVE response fields are guaranteed**: `answer`, `confidence`,
//    `run_id`, `intent`, `duration_ms`. Everything else - `row_count`,
//    `categories`, `tables`, `sql`, `retry_count`, `terminated_by` - ships only
//    while the server's `CHAT_INCLUDE_DIAGNOSTICS` flag is on, and the schema
//    description is explicit that they are *"omitted rather than nulled"*.
//
//    This is a live trap, not a hypothetical: a schema written from the field
//    list in the API hand-off doc PASSES in dev (diagnostics on) and throws
//    `ApiError { kind: 'validation' }` in any environment with them off - i.e.
//    the panel would work for us and be dead on arrival elsewhere. Every
//    diagnostic is therefore `.optional()`, and several are `.nullable()` too
//    because the OpenAPI declares `anyOf: [T, null]`.
//
// 2. **`confidence`, `intent` and `terminated_by` are OPEN STRINGS.** The values
//    in use today are enumerable (`high|medium|low`,
//    `data_question|chit_chat|out_of_scope|ambiguous`,
//    `guard_*`) but a closed `z.enum` would blank the operator's whole panel the
//    first time the chatbot team adds one. Narrowing happens downstream in
//    `services/aiIntent.service.ts`, which returns `undefined` on a miss and
//    lets the UI fall back to its conservative treatment. Same rule, same
//    reasoning as `module/marketops/schemas/webhook-outbox.schema.ts`.
//
//    Note `confidence` is a STRING on the wire, not a number.
//
// 3. **No `.default()` and no `.catch()` anywhere in a schema handed to the api
//    client.** `RequestOptions.schema` is typed `ZodType<T>`, which fixes
//    Input === Output; both combinators widen the input type, so the object stops
//    being assignable and you get a `TS2322` that reads as though the shape is
//    wrong. Use `.optional()` / `.nullable()` and handle absence downstream.
//
// 4. **snake_case preserved verbatim**, as on every other foreign surface here.
//    Renaming at the boundary would hide which fields are ours and which are the
//    server's, and this server's field set is expected to change.

import { z } from 'zod';

import { QUESTION_MAX_LENGTH, QUESTION_MIN_LENGTH } from '../constants';

// ───────────────────────────── request ──────────────────────────────────────

/**
 * One earlier turn, as the client recorded it.
 *
 * `role` genuinely IS a closed enum here - it is our own outbound value, not
 * something the server might extend under us.
 */
export const ChatTurnSchema = z.object({
  role: z.enum(['user', 'assistant']),
  content: z.string(),
});
export type ChatTurn = z.infer<typeof ChatTurnSchema>;

/**
 * The request body.
 *
 * `tenant_id` is in the BODY because that is what this host accepts today. Its
 * own OpenAPI description says *"Temporary: this belongs to the authenticated
 * principal, not to the request body"* - the service authors already consider it
 * wrong. It is confined to `api/aiChat.fetchers.ts` and fed from the verified
 * session claim so that moving it to a token is a one-file change.
 */
export const ChatRequestSchema = z.object({
  question: z.string().min(QUESTION_MIN_LENGTH).max(QUESTION_MAX_LENGTH),
  tenant_id: z.string().uuid(),
  history: z.array(ChatTurnSchema).optional(),
});
export type ChatRequest = z.infer<typeof ChatRequestSchema>;

// ───────────────────────────── response ─────────────────────────────────────

/**
 * The reply, and what the workflow did to reach it.
 *
 * Read rule 1 in the file header before touching the optionality here. The five
 * required fields are required; nothing else is.
 */
export const ChatReplySchema = z.object({
  /** Prose to display. May contain light Markdown; in practice it is sentences. */
  answer: z.string(),
  /** Open string. See rule 2 - and do NOT render it as a word. */
  confidence: z.string(),
  /** Always present. The handle a support ticket needs; surfaced via "Copy reference". */
  run_id: z.string(),
  /** Open string. Decides the bubble treatment. */
  intent: z.string(),
  /** Server-side processing time. Diagnostic, but always present. */
  duration_ms: z.number(),

  // ── diagnostics: present only while CHAT_INCLUDE_DIAGNOSTICS is on ──
  /** Rows the generated query returned. 0 when no query ran. */
  row_count: z.number().nullable().optional(),
  /**
   * Coarse data areas touched, e.g. ["accounts"].
   *
   * PARSED, NEVER RENDERED — same story as `sql` and `tables` below. These used
   * to be mapped through an i18n allowlist and shown as chips beside an answer;
   * the chips were removed on operator request, and the allowlist went with
   * them. The field stays because it is on the wire.
   */
  categories: z.array(z.string()).nullable().optional(),
  /**
   * Physical tables read, e.g. ["accounts.accounts"].
   *
   * PARSED, NEVER RENDERED. Table names ARE the shape of the database. This used
   * to appear inside a role-gated "How this answer was produced" disclosure; that
   * disclosure was removed on operator request, so today nothing in the UI reads
   * this field at all. It stays in the schema because it stays on the wire, and a
   * schema that omitted it would be lying about the contract — but there is no
   * surface to put it back on without a deliberate decision.
   */
  tables: z.array(z.string()).nullable().optional(),
  /**
   * The generated SQL.
   *
   * PARSED, NEVER RENDERED — same story as `tables`. The server's own schema
   * description warns that *"a response that carries the SQL is a response that
   * shows an end user the shape of the database"*, and with the disclosure gone
   * the safest possible treatment is the one now in force: it reaches the client
   * and stops there.
   */
  sql: z.string().nullable().optional(),
  /** How many times the server rebuilt its query before answering. PARSED, NEVER RENDERED. */
  retry_count: z.number().nullable().optional(),
  /** Which guard short-circuited the run, or null/absent if none did. */
  terminated_by: z.string().nullable().optional(),
});
export type ChatReply = z.infer<typeof ChatReplySchema>;

// ───────────────────────────── composer input ───────────────────────────────

/**
 * What the composer validates before spending a round trip.
 *
 * The bounds are the server's own (1..2000), mirrored so an over-length question
 * is refused locally instead of coming back as a 422 seven seconds later. Keep
 * them equal to `constants.ts`, which is keyed to the server's `maxLength`.
 */
export const AskQuestionSchema = z.object({
  question: z.string().trim().min(QUESTION_MIN_LENGTH).max(QUESTION_MAX_LENGTH),
});
export type AskQuestionInput = z.infer<typeof AskQuestionSchema>;
